import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Quiz } from "@/components/Quiz";
import { Seo } from "@/components/Seo";
import { ArrowLeft, ArrowRight, PlayCircle, CheckCircle2, Star, Lock, History, BookOpen } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function Lesson() {
  const { slug, token, lessonId } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [completed, setCompleted] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  useEffect(() => {
    setStatus("loading");
    setData(null);
    const url = token ? `/access/${token}/lessons/${lessonId}` : `/courses/${slug}/lessons/${lessonId}`;
    api.get(url)
      .then((r) => {
        setData(r.data);
        setCompleted(!!r.data.progress?.[lessonId]);
        setBookmarked(!!r.data.bookmarks?.[lessonId]);
        setStatus("ok");
      })
      .catch((e) => setStatus(e.response?.status === 403 ? "locked" : "error"));
  }, [slug, token, lessonId]);

  const toggleComplete = async () => {
    try {
      await api.post(`/access/${token}/progress`, { lesson_id: lessonId, completed: !completed });
      setCompleted(!completed);
      toast.success(!completed ? "Chapter marked complete." : "Marked as not complete.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update progress.");
    }
  };

  const toggleBookmark = async () => {
    try {
      await api.post(`/access/${token}/bookmark`, { lesson_id: lessonId, bookmarked: !bookmarked });
      setBookmarked(!bookmarked);
      toast.success(!bookmarked ? "Bookmarked for revision." : "Bookmark removed.");
    } catch {}
  };

  if (status === "locked") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6" data-testid="lesson-locked">
        <Lock size={32} className="text-brand" />
        <p className="font-display text-3xl font-bold tracking-tight">This lesson is locked.</p>
        <p className="max-w-md text-center text-sm leading-relaxed text-zinc-500">
          Buy the course to unlock every chapter, quiz and the question bank — or try the free preview lessons first.
        </p>
        <Link to={token ? `/access/${token}` : `/course/${slug}`} className="link-underline text-sm uppercase tracking-widest text-brand">
          Back to course
        </Link>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6" data-testid="lesson-error">
        <p className="font-display text-3xl font-bold tracking-tight">Lesson not found.</p>
        <Link to="/" className="link-underline text-sm uppercase tracking-widest text-brand">Back to home</Link>
      </div>
    );
  }

  if (!data) {
    return <div className="flex min-h-screen items-center justify-center text-zinc-600" data-testid="lesson-loading">Loading lesson...</div>;
  }

  const { lesson, siblings, index, total } = data;
  const paid = token ? !!data.paid : false;
  const backTo = token ? `/access/${token}` : `/course/${data.slug}`;
  const prev = siblings[index - 1];
  const next = siblings[index + 1];

  const nav = (sib, dir) => {
    if (!sib) return <span />;
    const allowed = paid || sib.free_preview;
    if (!allowed) {
      return <span className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-700" data-testid={`lesson-${dir}-locked`}><Lock size={12} /> Locked</span>;
    }
    const to = token ? `/access/${token}/learn/${sib.id}` : `/course/${data.slug}/learn/${sib.id}`;
    return (
      <Link to={to} data-testid={`lesson-${dir}-link`} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-300 hover:text-brand">
        {dir === "prev" ? <><ArrowLeft size={14} /> Previous chapter</> : <>Next chapter <ArrowRight size={14} /></>}
      </Link>
    );
  };

  return (
    <div className="min-h-screen px-6 py-16 md:px-12 md:py-20" data-testid="lesson-page">
      <Seo
        title={`${lesson.title} — ${data.course_title} | PP Academy`}
        description={`${lesson.title}: simplified textbook explanation from ${data.course_title} at PP Academy, with self-test quiz.`}
        path={token ? `/access/${token}/learn/${lessonId}` : `/course/${data.slug}/learn/${lessonId}`}
        noindex={!!token}
      />
      <div className="mx-auto max-w-4xl">
        <Link to={backTo} data-testid="lesson-back-link" className="mb-10 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-zinc-500 hover:text-white">
          <ArrowLeft size={14} /> {data.course_title}
        </Link>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
          <p className="text-xs font-medium uppercase tracking-[0.4em] text-brand" data-testid="lesson-chapter-label">
            Chapter {index + 1} of {total}
          </p>
          <h1 className="mt-4 font-display text-3xl font-black uppercase leading-tight tracking-tighter sm:text-4xl lg:text-5xl" data-testid="lesson-title">
            {lesson.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-5 text-sm text-zinc-500">
            <span className="flex items-center gap-2"><PlayCircle size={15} className="text-brand" /> {lesson.duration || "Self-paced"}</span>
            {lesson.updated_at && (
              <span className="flex items-center gap-2" data-testid="lesson-updated"><History size={15} className="text-brand" /> Updated {fmtDate(lesson.updated_at)}</span>
            )}
            {!token && <span className="bg-emerald-500/10 px-2.5 py-1 text-xs font-bold uppercase tracking-widest text-emerald-500" data-testid="lesson-free-badge">Free Preview</span>}
          </div>
        </motion.div>

        {lesson.video_url && (
          <a href={lesson.video_url} target="_blank" rel="noopener noreferrer" data-testid="lesson-video-link"
            className="mt-8 inline-flex items-center gap-3 border border-brand px-6 py-3 text-xs font-bold uppercase tracking-widest text-brand transition-colors duration-300 hover:bg-brand hover:text-white">
            <PlayCircle size={16} /> Watch Video Lesson
          </a>
        )}

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7, ease: EASE }} className="mt-10">
          <div className="flex items-center gap-3 border-b border-white/10 pb-4">
            <BookOpen size={18} className="text-brand" />
            <h2 className="font-display text-xl font-bold uppercase tracking-tight">Simplified Explanation</h2>
          </div>
          <p className="mt-4 text-xs uppercase tracking-widest text-zinc-600">Keep your textbook open alongside — this explains the same topic in simpler terms.</p>
          {lesson.explanation ? (
            <div className="rich-content mt-6" data-testid="lesson-explanation" dangerouslySetInnerHTML={{ __html: lesson.explanation }} />
          ) : (
            <div className="mt-6 border border-dashed border-white/15 p-8 text-sm leading-relaxed text-zinc-500" data-testid="lesson-explanation-empty">
              The mentor is writing the simplified explanation for this chapter — check back soon. Every topic here follows your textbook chapter by chapter.
            </div>
          )}
        </motion.div>

        {lesson.quiz?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7, ease: EASE }} className="mt-12">
            <Quiz quiz={lesson.quiz} submitUrl={paid ? `/access/${token}/lessons/${lessonId}/quiz` : `/courses/${data.slug}/lessons/${lessonId}/quiz`} />
          </motion.div>
        )}

        {paid && (
          <div className="mt-10 flex flex-wrap gap-4" data-testid="lesson-actions">
            <button
              onClick={toggleComplete}
              data-testid="mark-complete-button"
              className={`flex items-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${completed ? "bg-emerald-500/15 text-emerald-500 border border-emerald-500/40" : "bg-brand text-white hover:scale-95 transition-transform duration-300"}`}
            >
              <CheckCircle2 size={16} /> {completed ? "Completed" : "Mark as Complete"}
            </button>
            <button
              onClick={toggleBookmark}
              data-testid="bookmark-button"
              className={`flex items-center gap-2 border px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${bookmarked ? "border-amber-400/50 text-amber-400" : "border-white/15 text-zinc-300 hover:border-amber-400/50 hover:text-amber-400"}`}
            >
              <Star size={16} className={bookmarked ? "fill-amber-400" : ""} /> {bookmarked ? "Bookmarked" : "Bookmark"}
            </button>
          </div>
        )}

        <div className="mt-14 flex items-center justify-between border-t border-white/10 pt-8" data-testid="lesson-nav">
          {nav(prev, "prev")}
          {nav(next, "next")}
        </div>
      </div>
    </div>
  );
}
